import React, { useState, useEffect } from 'react';
import {
  Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, 
  FormControlLabel, Checkbox, Typography, Box, Paper, Accordion, 
  AccordionSummary, AccordionDetails, Chip, IconButton, List, ListItem, 
  ListItemText, ListItemSecondaryAction, Dialog, DialogTitle, DialogContent, 
  DialogActions, useTheme, alpha, Tooltip, Card, CardContent, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stack, Divider, Alert, CircularProgress, Collapse, Fade, Container
} from '@mui/material';
import { usePermissions } from '../../../utils/permissions';
import {
  ExpandMore as ExpandMoreIcon, Add as AddIcon, Delete as DeleteIcon, 
  Edit, Print as PrintIcon, CloudUpload as CloudUploadIcon, 
  GetApp as DownloadIcon, PictureAsPdf as PictureAsPdfIcon,
  Info as InfoOutlined, Cloud as CloudIcon, Build as BuildIcon, 
  People as PeopleIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon, Notes as NotesIcon, AttachFile as AttachFileIcon,
  ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon,
  ExpandLess as ExpandLessIcon, ContentCopy as ContentCopyIcon,
  Assignment as AssignmentIcon, Check as CheckIcon, Save as SaveIcon,
  BugReport as BugReportIcon, Close as CloseIcon, Cancel as CancelIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { executePrint } from './procedure-components/PrintProcedure';
import ProcedureTags from './procedure-components/ProcedureTags';

const ProcedureTab = ({ formulation, phases, showSnackbar, user, onUpdate }) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel, canApproveFormulations } = usePermissions();
  const [procedureCompleteLoading, setProcedureCompleteLoading] = useState(false);

  const defaultStepDetails = {
    step_title: '',
    instruction: '',
    expected_duration: '',
    criticality_level: 'Standard',
    
    // Environmental Conditions
    temperature_range: '',
    temperature_unit: 'Celsius',
    humidity_requirement: '',
    air_quality_requirement: '',
    lighting_requirement: '',
    cleanliness_level: '',
    ventilation_requirement: '',
    
    // Equipment
    primary_equipment: '',
    secondary_equipment: '',
    tools_required: '',
    calibration_requirements: '',
    
    // Personnel
    minimum_personnel_count: '',
    required_certifications: '',
    experience_level_required: '',
    supervision_requirements: '',
    
    // Safety
    ppe_requirements: '',
    hazard_identification: '',
    emergency_procedures: '',
    first_aid_requirements: '',
    
    // Process Control
    expected_results: '',
    critical_parameters: '',
    acceptance_criteria: '',
    verification_method: '',
    
    // Documentation
    documentation_level: 'Standard',
    record_keeping_requirements: '',
    approval_requirements: '',
    
    // Troubleshooting
    troubleshooting_notes: '',
    common_deviations: '',
    corrective_actions: '',
    escalation_procedures: '',
    
    // JSONB Fields
    step_tags: {},
    ph_details: {},
    qa_test_details: {},
    sample_details: {},
    stability_details: {},
    documents: [],
    
    order: 1,
    phase: null
  };

  const [steps, setSteps] = useState([]);
  const [openStepDialog, setOpenStepDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [documentError, setDocumentError] = useState('');
  const [phaseSelectionDialogOpen, setPhaseSelectionDialogOpen] = useState(false);
  const [phaseSearchQuery, setPhaseSearchQuery] = useState('');

  // Simple phases map without memoization
  const phasesMap = {};
  if (phases) {
    phases.forEach(phase => {
      phasesMap[phase.id] = phase;
    });
  }

  const showSaveStatus = (message, isError = false) => {
    setSaveMessage(message);
    setTimeout(() => setSaveMessage(''), 2000);
  };

  useEffect(() => {
    const loadProcedureSteps = async () => {
      if (!formulation?.id) return;

      try {
        const { data: procedureSteps, error } = await supabase
          .from('procedure_steps')
          .select('*')
          .eq('formulation_id', formulation.id)
          .order('step_order');

        if (error) throw error;

        const stepsWithDefaults = (procedureSteps || []).map(step => ({
          ...defaultStepDetails,
          ...step,
          step_tags: step.step_tags || defaultStepDetails.step_tags,
          ph_details: step.ph_details || {},
          qa_test_details: step.qa_test_details || {},
          sample_details: step.sample_details || {},
          stability_details: step.stability_details || {},
          documents: step.documents || [],
          order: step.step_order,
          phase: step.phase_id
        }));

        stepsWithDefaults.sort((a, b) => (a.order || 0) - (b.order || 0));
        setSteps(stepsWithDefaults);
      } catch (error) {
        console.error('Error loading procedure steps:', error);
        setSteps([]);
      }
    };

    loadProcedureSteps();
  }, [formulation?.id]);

  // Check permissions for procedures tab specifically
  const canViewProcedures = hasPermission('products.formulations.procedures.view', 'read');
  const canEditProcedures = hasPermission('products.formulations.procedures.edit', 'write');
  const canDeleteProcedures = hasPermission('products.formulations.procedures.delete', 'delete');
  const canApprove = canApproveFormulations();

  // Permission check
  if (!canViewProcedures) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
                        <Box sx={{ 
                p: 2, 
                borderRadius: 3, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                mb: 4,
                display: 'inline-flex',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
              }}>
            <AssignmentIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h4" sx={{ 
            mb: 2, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            mb: 2,
            fontWeight: 500,
            fontSize: '1rem'
          }}>
            Formulation Procedures Required
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.5,
            mb: 4,
            fontSize: '0.95rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to access the Formulation Procedures. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  // Manual save function - ONLY called when user clicks Save Step
  const saveSteps = async (steps) => {
    if (!formulation?.id) return;

    try {
      setIsSaving(true);
      
      for (const step of steps) {
        const stepData = {
          formulation_id: formulation.id,
          company_id: formulation.company_id,
          step_order: step.order || 1,
          step_title: step.step_title || '',
          instruction: step.instruction || '',
          expected_duration: step.expected_duration || '',
          criticality_level: step.criticality_level || 'Standard',
          phase_id: step.phase || null,
          
          // Environmental Conditions
          temperature_range: step.temperature_range || '',
          temperature_unit: step.temperature_unit || 'Celsius',
          humidity_requirement: step.humidity_requirement || '',
          air_quality_requirement: step.air_quality_requirement || '',
          lighting_requirement: step.lighting_requirement || '',
          cleanliness_level: step.cleanliness_level || '',
          ventilation_requirement: step.ventilation_requirement || '',
          
          // Equipment
          primary_equipment: step.primary_equipment || '',
          secondary_equipment: step.secondary_equipment || '',
          tools_required: step.tools_required || '',
          calibration_requirements: step.calibration_requirements || '',
          
          // Personnel
          minimum_personnel_count: step.minimum_personnel_count || '',
          required_certifications: step.required_certifications || '',
          experience_level_required: step.experience_level_required || '',
          supervision_requirements: step.supervision_requirements || '',
          
          // Safety
          ppe_requirements: step.ppe_requirements || '',
          hazard_identification: step.hazard_identification || '',
          emergency_procedures: step.emergency_procedures || '',
          first_aid_requirements: step.first_aid_requirements || '',
          
          // Process Control
          expected_results: step.expected_results || '',
          critical_parameters: step.critical_parameters || '',
          acceptance_criteria: step.acceptance_criteria || '',
          verification_method: step.verification_method || '',
          
          // Documentation
          documentation_level: step.documentation_level || 'Standard',
          record_keeping_requirements: step.record_keeping_requirements || '',
          approval_requirements: step.approval_requirements || '',
          
          // Troubleshooting
          troubleshooting_notes: step.troubleshooting_notes || '',
          common_deviations: step.common_deviations || '',
          corrective_actions: step.corrective_actions || '',
          escalation_procedures: step.escalation_procedures || '',
          
          // JSONB Fields - preserve existing data
          step_tags: step.step_tags || {},
          ph_details: step.ph_details || {},
          qa_test_details: step.qa_test_details || {},
          sample_details: step.sample_details || {},
          stability_details: step.stability_details || {},
          documents: step.documents || [],
          
          // User tracking
          ...(step.id ? { updated_by: user?.id } : { created_by: user?.id }),
        };

        if (step.id) {
          const { error } = await supabase
            .from('procedure_steps')
            .update(stepData)
            .eq('id', step.id);
          
          if (error) throw error;
        } else {
          const { data, error } = await supabase
            .from('procedure_steps')
            .insert(stepData)
            .select('id')
            .single();
          
          if (error) throw error;
          step.id = data.id;
        }
      }
      
      showSaveStatus('Procedure steps saved');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving steps:', error);
      showSaveStatus('Error saving steps', true);
    } finally {
      setIsSaving(false);
    }
  };

  // Simple handlers without memoization
  const handleStepToggle = (stepIndex) => (event) => {
    event.stopPropagation();
    setExpandedSteps(prev => ({
      ...prev,
      [stepIndex]: !prev[stepIndex]
    }));
  };

  const handleAddStep = () => {
    setCurrentStep({...defaultStepDetails, order: steps.length + 1});
    setIsEditMode(false);
    setOpenStepDialog(true);
  };

  const handleEditStep = (step) => {
    setCurrentStep(step);
    setIsEditMode(true);
    setOpenStepDialog(true);
  };

  const handleSaveStep = async () => {
    if (!currentStep) return;

    let updatedSteps;
    if (isEditMode) {
      updatedSteps = steps.map(step => 
        step.id === currentStep.id ? currentStep : step
      );
    } else {
      updatedSteps = [...steps, currentStep];
    }

    updatedSteps.sort((a, b) => (a.order || 0) - (b.order || 0));
    updatedSteps = updatedSteps.map((step, idx) => ({ ...step, order: idx + 1 }));

    setSteps(updatedSteps);
    // MANUAL SAVE - only when user clicks Save Step
    await saveSteps(updatedSteps);
    setOpenStepDialog(false);
    setCurrentStep(null);
  };

  const handleDeleteStep = async () => {
    if (stepToDelete === null) return;

    try {
      const stepToDeleteObj = steps[stepToDelete];
      
      // Delete all documents associated with this step from storage
      if (stepToDeleteObj?.documents && stepToDeleteObj.documents.length > 0) {
        const filePaths = stepToDeleteObj.documents
          .filter(doc => doc.path)
          .map(doc => doc.path);
        
        if (filePaths.length > 0) {
          try {
            const { error: storageError } = await supabase.storage
              .from('documents')
              .remove(filePaths);

            if (storageError) {
              console.error('Error deleting step documents from storage:', storageError);
            }
          } catch (storageDeleteError) {
            console.error('Error deleting documents:', storageDeleteError);
          }
        }
      }
      
      // Delete the step from database
      if (stepToDeleteObj?.id) {
        const { error } = await supabase
          .from('procedure_steps')
          .delete()
          .eq('id', stepToDeleteObj.id);
        
        if (error) throw error;
      }
    } catch (error) {
      console.error('Error deleting step:', error);
    }
    
    const updatedSteps = steps.filter((_, i) => i !== stepToDelete);
    const reorderedSteps = updatedSteps.map((step, idx) => ({ ...step, order: idx + 1 }));
    setSteps(reorderedSteps);
    setDeleteConfirmOpen(false);
    setStepToDelete(null);
  };

  const handleMoveStep = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;
    
    const updatedSteps = [...steps];
    
    [updatedSteps[index], updatedSteps[newIndex]] = [updatedSteps[newIndex], updatedSteps[index]];
    
    const reorderedSteps = updatedSteps.map((step, idx) => ({ ...step, order: idx + 1 }));
    setSteps(reorderedSteps);
  };

  const handleDuplicateStep = async (stepIndex) => {
    const stepToDuplicate = steps[stepIndex];
    const { id, created_at, updated_at, created_by, updated_by, ...stepData } = stepToDuplicate;
    const duplicatedStep = {
      ...stepData,
      step_title: `${stepData.step_title} (Copy)`,
      order: steps.length + 1
    };
    
    let updatedSteps = [...steps, duplicatedStep];
    updatedSteps.sort((a, b) => (a.order || 0) - (b.order || 0));
    updatedSteps = updatedSteps.map((step, idx) => ({ ...step, order: idx + 1 }));
    
    setSteps(updatedSteps);
    // Save the duplicated step to database
    await saveSteps(updatedSteps);
  };

  // Simple field change handler like StabilityStep
  const handleFieldChange = (field, value) => {
    setCurrentStep(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCurrentStepChange = (field, value) => {
    setCurrentStep(prev => ({ ...prev, [field]: value }));
  };

  const handlePrint = () => {
    executePrint(formulation, steps, phases);
  };

  const handleMarkProcedureComplete = async () => {
    if (!formulation?.id || !user?.id) return;
    
    setProcedureCompleteLoading(true);
    try {
      const newCompleteStatus = !formulation.procedure_complete;
      
      const { error } = await supabase
        .from('formulations')
        .update({
          procedure_complete: newCompleteStatus,
          procedure_completed_at: newCompleteStatus ? new Date().toISOString() : null,
          procedure_completed_by: newCompleteStatus ? user.id : null
        })
        .eq('id', formulation.id);
        
      if (error) throw error;
      
      showSnackbar(
        newCompleteStatus ? 'Procedure marked as complete!' : 'Procedure marked as incomplete', 
        'success'
      );
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating procedure completion:', error);
      showSnackbar('Error updating procedure status: ' + error.message, 'error');
    } finally {
      setProcedureCompleteLoading(false);
    }
  };

  // Document handling functions
  const uploadDocument = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `procedure-documents/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('documents')
      .getPublicUrl(filePath);

    return {
      name: file.name,
      url: data.publicUrl,
      path: filePath,
      type: file.type,
      size: file.size,
      uploadedAt: new Date().toISOString()
    };
  };

  const handleRemoveDocument = async (documentIndex) => {
    const updatedDocuments = [...(currentStep.documents || [])];
    const documentToDelete = updatedDocuments[documentIndex];
    
    // Delete from storage if it has a path
    if (documentToDelete?.path) {
      try {
        const { error: storageError } = await supabase.storage
          .from('documents')
          .remove([documentToDelete.path]);

        if (storageError) {
          console.error('Error deleting document from storage:', storageError);
        }
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    }
    
    updatedDocuments.splice(documentIndex, 1);
    
    setCurrentStep({
      ...currentStep,
      documents: updatedDocuments
    });
    setDocumentError('');
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate files before upload
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setDocumentError('File size must be less than 10MB');
        return;
      }
      
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setDocumentError('Only PDF, images, text, and Word documents are allowed');
        return;
      }
    }

    try {
      setUploadingFiles(true);
      setDocumentError('');
      
      const uploadPromises = files.map(file => uploadDocument(file));
      const uploadedDocs = await Promise.all(uploadPromises);
      
      const updatedDocuments = [...(currentStep?.documents || []), ...uploadedDocs];
      setCurrentStep({
        ...currentStep,
        documents: updatedDocuments
      });
    } catch (err) {
      console.error('Error uploading files:', err);
      setDocumentError('Failed to upload files. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
    
    // Reset the input
    event.target.value = '';
  };

  const handleViewDocument = async (doc) => {
    try {
      const filePath = doc.path || doc.url.split('/').pop();
      const { data, error } = await supabase.storage
        .from('documents')
        .download(filePath);

      if (error) throw error;

      const blobUrl = URL.createObjectURL(data);
      const newWindow = window.open(blobUrl, '_blank');
      
      if (!newWindow) {
        handleDownloadDocument(doc);
      }
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('View error:', err);
      setDocumentError('File view failed');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const filePath = doc.path || doc.url.split('/').pop();
      const { data, error } = await supabase.storage
        .from('documents')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setDocumentError('File download failed');
    }
  };

  const renderStepDetails = (step) => {
    const sections = [
      {
        title: 'Basic Information',
        icon: <InfoOutlined />,
        color: theme.palette.primary.main,
        fields: [
          { label: 'Instructions', value: step.instruction },
          { label: 'Expected Duration', value: step.expected_duration },
          { label: 'Phase', value: phasesMap[step.phase]?.name || 'Unassigned' },
          { label: 'Criticality Level', value: step.criticality_level }
        ]
      },
      {
        title: 'Environmental Conditions',
        icon: <CloudIcon />,
        color: theme.palette.success.main,
        fields: [
          { label: 'Temperature Range', value: step.temperature_range ? `${step.temperature_range} ${step.temperature_unit === 'Celsius' ? '°C' : step.temperature_unit === 'Fahrenheit' ? '°F' : 'K'}` : '' },
          { label: 'Humidity', value: step.humidity_requirement },
          { label: 'Air Quality', value: step.air_quality_requirement },
          { label: 'Lighting', value: step.lighting_requirement },
          { label: 'Cleanliness', value: step.cleanliness_level },
          { label: 'Ventilation', value: step.ventilation_requirement }
        ]
      },
      {
        title: 'Equipment Requirements',
        icon: <BuildIcon />,
        color: '#ffa726',
        fields: [
          { label: 'Primary Equipment', value: step.primary_equipment },
          { label: 'Secondary Equipment', value: step.secondary_equipment },
          { label: 'Tools Required', value: step.tools_required },
          { label: 'Calibration', value: step.calibration_requirements }
        ]
      },
      {
        title: 'Personnel Requirements',
        icon: <PeopleIcon />,
        color: theme.palette.warning.main,
        fields: [
          { label: 'Minimum Personnel', value: step.minimum_personnel_count },
          { label: 'Certifications', value: step.required_certifications },
          { label: 'Experience Level', value: step.experience_level_required },
          { label: 'Supervision', value: step.supervision_requirements }
        ]
      },
      {
        title: 'Safety Requirements',
        icon: <WarningIcon />,
        color: theme.palette.error.main,
        fields: [
          { label: 'PPE Requirements', value: step.ppe_requirements },
          { label: 'Hazards', value: step.hazard_identification },
          { label: 'Emergency Procedures', value: step.emergency_procedures },
          { label: 'First Aid', value: step.first_aid_requirements }
        ]
      },
      {
        title: 'Process Control',
        icon: <CheckCircleIcon />,
        color: theme.palette.info.main,
        fields: [
          { label: 'Expected Results', value: step.expected_results },
          { label: 'Critical Parameters', value: step.critical_parameters },
          { label: 'Acceptance Criteria', value: step.acceptance_criteria },
          { label: 'Verification Method', value: step.verification_method }
        ]
      },
      {
        title: 'Documentation Requirements',
        icon: <DescriptionIcon />,
        color: '#9c27b0',
        fields: [
          { label: 'Documentation Level', value: step.documentation_level },
          { label: 'Record Keeping', value: step.record_keeping_requirements },
          { label: 'Approvals', value: step.approval_requirements }
        ]
      },
      {
        title: 'Troubleshooting',
        icon: <BugReportIcon />,
        color: '#795548',
        fields: [
          { label: 'Troubleshooting Notes', value: step.troubleshooting_notes },
          { label: 'Common Deviations', value: step.common_deviations },
          { label: 'Corrective Actions', value: step.corrective_actions },
          { label: 'Escalation Procedures', value: step.escalation_procedures }
        ]
      }
    ];

    return (
                    <Box sx={{ p: 2 }}>
                {sections.map((section, sectionIndex) => {
          const hasContent = section.fields.some(field => field.value && field.value.trim());
          const hasDocuments = section.title === 'Documentation Requirements' && step.documents && step.documents.length > 0;
          
          if (!hasContent && !hasDocuments) return null;

          return (
            <Paper key={sectionIndex} sx={{ 
              mb: 2,
              borderRadius: 3,
              border: `1px solid ${alpha(section.color, 0.3)}`,
              background: `linear-gradient(135deg, ${alpha(section.color, 0.08)}, ${alpha(section.color, 0.04)})`,
              boxShadow: `0 12px 32px ${alpha(section.color, 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 20px 40px ${alpha(section.color, 0.25)}`,
                border: `1px solid ${alpha(section.color, 0.5)}`
              },
              overflow: 'hidden'
            }}>
              <TableContainer sx={{ overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        colSpan={2}
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.95rem',
                          bgcolor: `linear-gradient(135deg, ${alpha(section.color, 0.12)}, ${alpha(section.color, 0.08)})`,
                          borderBottom: `1px solid ${section.color}`,
                          color: section.color,
                          py: 2,
                          px: 3
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {section.icon}
                          {section.title}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.fields.map((field, fieldIndex) => {
                      if (!field.value || !field.value.trim()) return null;
                      
                      return (
                        <TableRow key={fieldIndex} hover sx={{ 
                          '&:hover': { 
                            bgcolor: alpha(section.color, 0.05),
                            transform: 'translateX(4px)',
                            transition: 'all 0.2s ease',
                            border: `1px solid ${section.color}`
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2.5,
                            px: 3
                          }
                        }}>
                          <TableCell sx={{ fontWeight: 500, width: '25%' }}>{field.label}</TableCell>
                          <TableCell sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: '#495057' }}>
                            {field.value}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Add documents to Documentation Requirements section */}
                    {section.title === 'Documentation Requirements' && step.documents && step.documents.length > 0 && (
                      <TableRow hover sx={{ 
                        '&:hover': { 
                          bgcolor: alpha(section.color, 0.05),
                          transform: 'translateX(4px)',
                          transition: 'all 0.2s ease',
                          border: `1px solid ${section.color}`
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2.5,
                          px: 3
                        }
                      }}>
                        <TableCell sx={{ fontWeight: 500, width: '25%' }}>Attached Documents</TableCell>
                        <TableCell sx={{ lineHeight: 1.6, color: '#495057' }}>
                          <Stack spacing={1}>
                            {step.documents.map((doc, docIndex) => (
                              <Box 
                                key={docIndex}
                                sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: '#f5f5f5',
                                  border: 1,
                                  borderColor: '#e0e0e0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2
                                }}
                              >
                                <Box sx={{ 
                                  width: 32, 
                                  height: 32, 
                                  bgcolor: 'white',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: 1,
                                  borderColor: '#e0e0e0'
                                }}>
                                  {doc.type?.includes('pdf') ? (
                                    <PictureAsPdfIcon sx={{ fontSize: 20, color: '#d32f2f' }} />
                                  ) : (
                                    <AttachFileIcon sx={{ fontSize: 20, color: '#666' }} />
                                  )}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                    {doc.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                    {doc.size ? `${(doc.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="View Document">
                                    <IconButton 
                                      size="small"
                                      onClick={() => handleViewDocument(doc)}
                                      sx={{ 
                                        bgcolor: 'white',
                                        border: 1,
                                        borderColor: '#e0e0e0',
                                        color: '#666',
                                        '&:hover': { bgcolor: '#f0f0f0' },
                                        width: 32,
                                        height: 32
                                      }}
                                    >
                                      {doc.type?.includes('pdf') ? (
                                        <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                                      ) : (
                                        <AttachFileIcon sx={{ fontSize: 16 }} />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download Document">
                                    <IconButton 
                                      size="small"
                                      onClick={() => handleDownloadDocument(doc)}
                                      sx={{ 
                                        bgcolor: 'white',
                                        border: 1,
                                        borderColor: '#e0e0e0',
                                        color: '#666',
                                        '&:hover': { bgcolor: '#f0f0f0' },
                                        width: 32,
                                        height: 32
                                      }}
                                    >
                                      <DownloadIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          );
        })}
      </Box>
    );
  };



  return (
    <Box 
      sx={{ 
        pb: 4,
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Box sx={{ maxWidth: '100vw', width: '100%', px: { xs: 2, sm: 3 } }}>
      {/* Enhanced Header Section */}
              <Card 
        sx={{ 
          borderRadius: 3,
          mb: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <AssignmentIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    Mixing Procedure
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Comprehensive step-by-step mixing instructions with safety and quality controls
                  </Typography>
                </Box>
              </Stack>
              
              {/* Quick Stats */}
              <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {steps.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Steps
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {steps.filter(step => step.criticality_level === 'Critical').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Critical Steps
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500, 
                    color: phases?.length > 0 ? theme.palette.primary.main : 'text.disabled'
                  }}>
                    {phases?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Phases
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                disabled={steps.length === 0}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  background: 'white',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  backdropFilter: 'blur(10px)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                  '&:hover': {
                    background: 'white',
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Print Procedure
              </Button>
              {steps.length > 0 && (canEditProcedures || canApprove) && (
                <Button
                  variant={formulation?.procedure_complete ? "contained" : "outlined"}
                  startIcon={formulation?.procedure_complete ? <CheckCircleIcon /> : <CheckIcon />}
                  onClick={handleMarkProcedureComplete}
                  disabled={procedureCompleteLoading}
                  sx={{ 
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    background: formulation?.procedure_complete 
                      ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                      : 'white',
                    border: formulation?.procedure_complete 
                      ? 'none'
                      : `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    color: formulation?.procedure_complete ? 'white' : theme.palette.success.main,
                    fontWeight: 500,
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.15)}`,
                    '&:hover': {
                      background: formulation?.procedure_complete 
                        ? `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`
                        : alpha(theme.palette.success.main, 0.08),
                      borderColor: formulation?.procedure_complete ? 'none' : theme.palette.success.main,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.25)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {procedureCompleteLoading ? 'Updating...' : 
                   formulation?.procedure_complete ? 'Mark Incomplete' : 'Mark Complete'}
                </Button>
              )}
              {canEditProcedures && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddStep}
                  sx={{ 
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    fontWeight: 500,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Add Step
                </Button>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Steps List */}
      {steps.length === 0 ? (
        <Paper sx={{ 
          p: 6, 
          textAlign: 'center', 
          backgroundColor: alpha(theme.palette.grey[50], 0.5),
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <AssignmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No procedure steps defined yet
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Start building your manufacturing procedure by adding the first step
          </Typography>
          {canEditProcedures && (
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleAddStep}
              sx={{ 
                borderRadius: 3,
                px: 3,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                fontWeight: 500,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                },
                transition: 'all 0.2s ease'
              }}
            >
              Add First Step
            </Button>
          )}
        </Paper>
      ) : (
        <Stack spacing={2}>
          {steps.map((step, index) => (
            <Paper 
              key={step.id || index}
              elevation={2}
              sx={{ 
                borderRadius: 3,
                overflow: 'hidden',
                border: step.criticality_level === 'Critical' ? `1px solid ${theme.palette.error.main}` : 
                       step.criticality_level === 'High' ? `1px solid ${theme.palette.warning.main}` : 
                       `1px solid ${theme.palette.grey[400]}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <Accordion 
                expanded={expandedSteps[index] || false}
                onChange={handleStepToggle(index)}
                sx={{ 
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  backgroundColor: 'transparent'
                }}
              >
                <AccordionSummary 
                  expandIcon={expandedSteps[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ 
                    px: 3, 
                    py: 2,
                    backgroundColor: step.criticality_level === 'Critical' ? alpha(theme.palette.error.main, 0.05) :
                                   step.criticality_level === 'High' ? alpha(theme.palette.warning.main, 0.05) :
                                   alpha(theme.palette.primary.main, 0.03)
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip 
                        label={`Step ${step.order || index + 1}`}
                        size="small"
                        sx={{ 
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                      <Typography variant="h6" fontWeight={500}>
                        {step.step_title || `Step ${step.order || index + 1}`}
                      </Typography>
                      {step.criticality_level && step.criticality_level !== 'Standard' && (
                        <Chip
                          label={step.criticality_level}
                          size="small"
                          color={step.criticality_level === 'Critical' ? 'error' : 'warning'}
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                      {phasesMap[step.phase] && (
                        <Chip
                          label={phasesMap[step.phase].name}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: phasesMap[step.phase].color, color: phasesMap[step.phase].color }}
                        />
                      )}
                    </Box>
                    
                    <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                      {canEditProcedures && (
                        <Tooltip title="Edit Step">
                          <IconButton size="small" onClick={() => handleEditStep(step)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEditProcedures && (
                        <Tooltip title="Duplicate Step">
                          <IconButton size="small" onClick={() => handleDuplicateStep(index)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEditProcedures && (
                        <Tooltip title="Move Up">
                          <IconButton 
                            size="small" 
                            onClick={() => handleMoveStep(index, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEditProcedures && (
                        <Tooltip title="Move Down">
                          <IconButton 
                            size="small" 
                            onClick={() => handleMoveStep(index, 'down')}
                            disabled={index === steps.length - 1}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeleteProcedures && (
                        <Tooltip title="Delete Step">
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setStepToDelete(index);
                              setDeleteConfirmOpen(true);
                            }}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ p: 0 }}>
                  <Collapse in={expandedSteps[index]} timeout="auto" unmountOnExit>
                    {renderStepDetails(step)}
                    <Box sx={{ px: 3, pb: 3 }}>
                      <ProcedureTags
                        currentStep={step}
                        formulation={formulation}
                        canEdit={canEditProcedures}
                        canDelete={canDeleteProcedures}
                        onStepUpdate={() => {
                          // Refresh steps when QC components are updated
                          const loadProcedureSteps = async () => {
                            if (!formulation?.id) return;
                            
                            try {
                              const { data: procedureSteps, error } = await supabase
                                .from('procedure_steps')
                                .select('*')
                                .eq('formulation_id', formulation.id)
                                .order('step_order');

                              if (error) throw error;

                              const stepsWithDefaults = (procedureSteps || []).map(step => ({
                                ...defaultStepDetails,
                                ...step,
                                step_tags: step.step_tags || defaultStepDetails.step_tags,
                                ph_details: step.ph_details || {},
                                qa_test_details: step.qa_test_details || {},
                                sample_details: step.sample_details || {},
                                stability_details: step.stability_details || {},
                                documents: step.documents || [],
                                order: step.step_order,
                                phase: step.phase_id
                              }));

                              stepsWithDefaults.sort((a, b) => (a.order || 0) - (b.order || 0));
                              setSteps(stepsWithDefaults);
                            } catch (error) {
                              console.error('Error loading procedure steps:', error);
                            }
                          };
                          loadProcedureSteps();
                        }}
                      />
                    </Box>
                  </Collapse>
                </AccordionDetails>
              </Accordion>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Add/Edit Step Dialog */}
      <Dialog 
        open={openStepDialog} 
        onClose={() => setOpenStepDialog(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', bgcolor: 'grey.50' } }}
      >
        <DialogTitle sx={{ p: 2, fontWeight: 500, borderBottom: '1px solid #e0e0e0', bgcolor: 'white' }}>
          {isEditMode ? 'Edit Step' : 'Add New Step'}
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'grey.50' }}>
          <Box sx={{ p: 2 }}>
            {/* Basic Information Section */}
            <Paper sx={{ 
              mb: 2, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 2, 
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.04)})`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                  Basic Information
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Step Title"
                      fullWidth
                      value={currentStep?.step_title || ''}
                      onChange={(e) => handleFieldChange('step_title', e.target.value)}
                      size="small"
                      sx={{
                        height: 56,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: 2,
                          height: 56,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      onClick={() => setPhaseSelectionDialogOpen(true)}
                      sx={{ 
                        height: 56,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1,
                        color: currentStep?.phase ? theme.palette.text.primary : theme.palette.text.secondary,
                        fontWeight: 500,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      {currentStep?.phase 
                        ? phases?.find(phase => phase.id === currentStep.phase)?.name || 'Select Phase'
                        : 'Select Phase'
                      }
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Instructions"
                      fullWidth
                      multiline
                      rows={4}
                      value={currentStep?.instruction || ''}
                      onChange={(e) => handleFieldChange('instruction', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Expected Duration"
                      fullWidth
                      value={currentStep?.expected_duration || ''}
                      onChange={(e) => handleFieldChange('expected_duration', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Criticality Level</InputLabel>
                      <Select
                        label="Criticality Level"
                        value={currentStep?.criticality_level || 'Standard'}
                        onChange={(e) => handleFieldChange('criticality_level', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="Standard">Standard</MenuItem>
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Critical">Critical</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Environmental Conditions Section */}
            <Paper sx={{ 
              mb: 2, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.1)}`,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.12)}, ${alpha(theme.palette.success.main, 0.04)})`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                  Environmental Conditions
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Temperature Range"
                      fullWidth
                      value={currentStep?.temperature_range || ''}
                      onChange={(e) => handleFieldChange('temperature_range', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.success.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unit</InputLabel>
                      <Select
                        label="Unit"
                        value={currentStep?.temperature_unit || 'Celsius'}
                        onChange={(e) => handleFieldChange('temperature_unit', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="Celsius">Celsius (°C)</MenuItem>
                        <MenuItem value="Fahrenheit">Fahrenheit (°F)</MenuItem>
                        <MenuItem value="Kelvin">Kelvin (K)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Humidity Requirement"
                      fullWidth
                      value={currentStep?.humidity_requirement || ''}
                      onChange={(e) => handleFieldChange('humidity_requirement', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.success.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Air Quality Requirement"
                      fullWidth
                      value={currentStep?.air_quality_requirement || ''}
                      onChange={(e) => handleFieldChange('air_quality_requirement', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.success.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Lighting Requirement"
                      fullWidth
                      value={currentStep?.lighting_requirement || ''}
                      onChange={(e) => handleFieldChange('lighting_requirement', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.success.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Cleanliness Level"
                      fullWidth
                      value={currentStep?.cleanliness_level || ''}
                      onChange={(e) => handleFieldChange('cleanliness_level', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.success.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Ventilation Requirement"
                      fullWidth
                      value={currentStep?.ventilation_requirement || ''}
                      onChange={(e) => handleFieldChange('ventilation_requirement', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.success.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Equipment Section */}
            <Paper sx={{ 
              mb: 2, 
              borderRadius: 3,
              border: `1px solid ${alpha('#ffa726', 0.2)}`,
              background: `linear-gradient(135deg, ${alpha('#ffa726', 0.08)}, ${alpha('#ffa726', 0.02)})`,
              boxShadow: `0 4px 20px ${alpha('#ffa726', 0.1)}`,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha('#ffa726', 0.1)}`,
                background: `linear-gradient(135deg, ${alpha('#ffa726', 0.12)}, ${alpha('#ffa726', 0.04)})`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#ffa726' }}>
                  Equipment Requirements
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Primary Equipment"
                      fullWidth
                      value={currentStep?.primary_equipment || ''}
                      onChange={(e) => handleFieldChange('primary_equipment', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#ffa726', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#ffa726', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#ffa726', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Secondary Equipment"
                      fullWidth
                      value={currentStep?.secondary_equipment || ''}
                      onChange={(e) => handleFieldChange('secondary_equipment', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#ffa726', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#ffa726', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#ffa726', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Tools Required"
                      fullWidth
                      value={currentStep?.tools_required || ''}
                      onChange={(e) => handleFieldChange('tools_required', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#ffa726', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#ffa726', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#ffa726', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Calibration Requirements"
                      fullWidth
                      value={currentStep?.calibration_requirements || ''}
                      onChange={(e) => handleFieldChange('calibration_requirements', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#ffa726', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#ffa726', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#ffa726', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Personnel Section */}
            <Paper sx={{ 
              mb: 2, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.1)}`,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.12)}, ${alpha(theme.palette.warning.main, 0.04)})`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'warning.main' }}>
                  Personnel Requirements
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Minimum Personnel Count"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.minimum_personnel_count || ''}
                      onChange={(e) => handleFieldChange('minimum_personnel_count', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.warning.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Required Certifications"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.required_certifications || ''}
                      onChange={(e) => handleFieldChange('required_certifications', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.warning.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Experience Level Required"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.experience_level_required || ''}
                      onChange={(e) => handleFieldChange('experience_level_required', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.warning.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Supervision Requirements"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.supervision_requirements || ''}
                      onChange={(e) => handleFieldChange('supervision_requirements', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.warning.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Safety Section */}
            <Paper sx={{ 
              mb: 2, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.02)})`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.error.main, 0.1)}`,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.12)}, ${alpha(theme.palette.error.main, 0.04)})`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'error.main' }}>
                  Safety Requirements
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="PPE Requirements"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.ppe_requirements || ''}
                      onChange={(e) => handleFieldChange('ppe_requirements', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.error.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Hazard Identification"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.hazard_identification || ''}
                      onChange={(e) => handleFieldChange('hazard_identification', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.error.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Emergency Procedures"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.emergency_procedures || ''}
                      onChange={(e) => handleFieldChange('emergency_procedures', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.error.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="First Aid Requirements"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.first_aid_requirements || ''}
                      onChange={(e) => handleFieldChange('first_aid_requirements', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.error.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Process Control Section */}
            <Paper sx={{ 
              mb: 2, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.1)}`,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.12)}, ${alpha(theme.palette.info.main, 0.04)})`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'info.main' }}>
                  Process Control
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Expected Results"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.expected_results || ''}
                      onChange={(e) => handleFieldChange('expected_results', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.info.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Critical Parameters"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.critical_parameters || ''}
                      onChange={(e) => handleFieldChange('critical_parameters', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.info.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Acceptance Criteria"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.acceptance_criteria || ''}
                      onChange={(e) => handleFieldChange('acceptance_criteria', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.info.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Verification Method"
                      fullWidth
                      multiline
                      rows={2}
                      value={currentStep?.verification_method || ''}
                      onChange={(e) => handleFieldChange('verification_method', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha(theme.palette.info.main, 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Documentation Section */}
            <Paper sx={{ 
              mb: 2, 
              borderRadius: 3,
              border: `1px solid ${alpha('#9c27b0', 0.2)}`,
              background: `linear-gradient(135deg, ${alpha('#9c27b0', 0.08)}, ${alpha('#9c27b0', 0.02)})`,
              boxShadow: `0 4px 20px ${alpha('#9c27b0', 0.1)}`,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha('#9c27b0', 0.1)}`,
                background: `linear-gradient(135deg, ${alpha('#9c27b0', 0.12)}, ${alpha('#9c27b0', 0.04)})`
              }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <NotesIcon sx={{ color: '#9c27b0' }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#9c27b0' }}>
                    Documentation Requirements
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Documentation Level</InputLabel>
                      <Select
                        label="Documentation Level"
                        value={currentStep?.documentation_level || 'Standard'}
                        onChange={(e) => handleFieldChange('documentation_level', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="Minimal">Minimal</MenuItem>
                        <MenuItem value="Standard">Standard</MenuItem>
                        <MenuItem value="Detailed">Detailed</MenuItem>
                        <MenuItem value="Comprehensive">Comprehensive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Record Keeping Requirements"
                      fullWidth
                      value={currentStep?.record_keeping_requirements || ''}
                      onChange={(e) => handleFieldChange('record_keeping_requirements', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#9c27b0', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#9c27b0', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#9c27b0', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Approval Requirements"
                      fullWidth
                      value={currentStep?.approval_requirements || ''}
                      onChange={(e) => handleFieldChange('approval_requirements', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#9c27b0', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#9c27b0', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#9c27b0', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Document Upload Section */}
                <Box sx={{ mt: 4 }}>
                                            <Alert severity="info" sx={{ mb: 3 }}>
                            Attach supporting documents like SOPs, forms, or reference materials for this step. 
                            Supported formats: PDF, images, text files, and Word documents (max 10MB each).
                          </Alert>
                  
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <input
                            accept=".pdf,.jpg,.jpeg,.png,.txt,.doc,.docx"
                            style={{ display: 'none' }}
                            id="document-upload"
                            multiple
                            type="file"
                            onChange={handleFileUpload}
                          />
                      <label htmlFor="document-upload">
                        <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />}>
                          Upload Documents
                        </Button>
                      </label>
                      
                      {uploadingFiles && <CircularProgress size={20} />}
                    </Box>

                    {(currentStep?.documents || []).map((doc, index) => (
                                                  <Paper key={index} elevation={1} sx={{ 
                              p: 2, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              borderRadius: 2
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {doc.type?.includes('pdf') ? <PictureAsPdfIcon color="error" /> : <AttachFileIcon />}
                                <Typography variant="body2" fontWeight={500}>
                                  {doc.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({(doc.size / 1024).toFixed(1)} KB)
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="View Document">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleViewDocument(doc)}
                                    color="primary"
                                  >
                                    {doc.type?.includes('pdf') ? (
                                      <PictureAsPdfIcon fontSize="small" />
                                    ) : (
                                      <AttachFileIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download Document">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDownloadDocument(doc)}
                                    color="info"
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove Document">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleRemoveDocument(index)}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Paper>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Paper>

            {/* Troubleshooting Section */}
            <Paper sx={{ 
              mb: 2, 
              borderRadius: 3,
              border: `1px solid ${alpha('#795548', 0.2)}`,
              background: `linear-gradient(135deg, ${alpha('#795548', 0.08)}, ${alpha('#795548', 0.02)})`,
              boxShadow: `0 4px 20px ${alpha('#795548', 0.1)}`,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha('#795548', 0.1)}`,
                background: `linear-gradient(135deg, ${alpha('#795548', 0.12)}, ${alpha('#795548', 0.04)})`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#795548' }}>
                  Troubleshooting & Problem Resolution
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Troubleshooting Notes"
                      fullWidth
                      multiline
                      rows={3}
                      value={currentStep?.troubleshooting_notes || ''}
                      onChange={(e) => handleFieldChange('troubleshooting_notes', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#795548', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#795548', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#795548', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Common Deviations"
                      fullWidth
                      multiline
                      rows={3}
                      value={currentStep?.common_deviations || ''}
                      onChange={(e) => handleFieldChange('common_deviations', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#795548', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#795548', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#795548', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Corrective Actions"
                      fullWidth
                      multiline
                      rows={3}
                      value={currentStep?.corrective_actions || ''}
                      onChange={(e) => handleFieldChange('corrective_actions', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#795548', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#795548', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#795548', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Escalation Procedures"
                      fullWidth
                      multiline
                      rows={3}
                      value={currentStep?.escalation_procedures || ''}
                      onChange={(e) => handleFieldChange('escalation_procedures', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: alpha(theme.palette.background.paper, 0.8),
                          backdropFilter: 'blur(8px)',
                          borderRadius: 3,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha('#795548', 0.15)}`
                          },
                          '&.Mui-focused': {
                            boxShadow: `0 0 0 3px ${alpha('#795548', 0.1)}`
                          },
                          '& fieldset': {
                            borderColor: alpha('#795548', 0.3),
                            borderWidth: 1.5
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 2, borderTop: '1px solid #e0e0e0', bgcolor: 'white' }}>
          <Button onClick={() => setOpenStepDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveStep}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 120
            }}
          >
            {isSaving ? 'Saving...' : 'Save Step'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase Selection Dialog */}
      <Dialog
        open={phaseSelectionDialogOpen}
        onClose={() => {
          setPhaseSelectionDialogOpen(false);
          setPhaseSearchQuery('');
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
                          p: 2, 
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          Select Phase
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              placeholder="Search phases..."
              value={phaseSearchQuery}
              onChange={(e) => setPhaseSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            maxHeight: 400, 
            overflowY: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            borderRadius: 2,
            background: 'white',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none'
          }}>
            {/* No Phase Option */}
            <Box
              onClick={() => {
                handleFieldChange('phase', '');
                setPhaseSelectionDialogOpen(false);
                setPhaseSearchQuery('');
              }}
              sx={{
                p: 3,
                borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  transform: 'translateX(4px)',
                  boxShadow: 'inset 4px 0 0 black'
                }
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5, fontStyle: 'italic' }}>
                No Phase
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Assign this step to no specific phase
              </Typography>
            </Box>
            
            {phases?.filter(phase => 
              phase.name.toLowerCase().includes(phaseSearchQuery.toLowerCase()) ||
              (phase.description && phase.description.toLowerCase().includes(phaseSearchQuery.toLowerCase()))
            ).map(phase => (
              <Box
                key={phase.id}
                onClick={() => {
                  handleFieldChange('phase', phase.id);
                  setPhaseSelectionDialogOpen(false);
                  setPhaseSearchQuery('');
                }}
                sx={{
                  p: 3,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {phase.name}
                </Typography>
                {phase.description && (
                  <Typography variant="body2" color="text.secondary">
                    {phase.description}
                  </Typography>
                )}
              </Box>
            ))}
            
            {phases?.filter(phase => 
              phase.name.toLowerCase().includes(phaseSearchQuery.toLowerCase()) ||
              (phase.description && phase.description.toLowerCase().includes(phaseSearchQuery.toLowerCase()))
            ).length === 0 && phaseSearchQuery && (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No phases found matching your search.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => {
              setPhaseSelectionDialogOpen(false);
              setPhaseSearchQuery('');
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Confirm Delete
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary', mt: 1 }}>
            Are you sure you want to delete this step? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid', 
          borderColor: 'divider', 
          p: 2, 
          justifyContent: 'space-between' 
        }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ 
              px: 3,
              borderRadius: 3,
              py: 1.5
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleDeleteStep}
            sx={{
              px: 3,
              borderRadius: 3,
              py: 1.5,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Delete Step
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Status */}
      {saveMessage && (
        <Fade in={!!saveMessage}>
          <Box sx={{ 
            position: 'fixed', 
            top: 20, 
            right: 20, 
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'success.main',
            color: 'white',
            px: 3,
            py: 2,
            borderRadius: 2,
            boxShadow: 3
          }}>
            <CheckIcon fontSize="small" />
            <Typography variant="body2" fontWeight={500}>
              {saveMessage}
            </Typography>
          </Box>
        </Fade>
      )}
      </Box>
    </Box>
  );
};

export default ProcedureTab;